import { EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Switch,
  Text,
  Tooltip,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { PatientDTO } from "@metriport/api-sdk";
import { useEffect } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiInfo } from "react-icons/fi";
import { PatientTab } from "../shared-logic/consolidated-context/reducer";
import { useRefAndUpdateHeight } from "../shared-logic/elementHeight";
import { useSharedContext } from "../shared-logic/shared-context";
import { Button as MetriportButton } from "./button";
import { IdContainer } from "./id-copy-container";

type HeaderActions = {
  onQueryPatientDocuments: () => Promise<void>;
  onGenerateMedicalRecord: () => void;
  onEditPatient: () => void;
  onDisplayPatientDemos: () => void;
  onViewPatientMatches: () => void;
  onSetPatientTab: (tab: PatientTab) => void;
  toggleHieOptOut: (hieOptOut: boolean) => Promise<void>;
  toggleUsingSectionSearch?: () => void;
  onEditCustomKeywords?: () => void;
  setUsingMetriportOnlyBundleFromConsolidated?: (
    usingMetriportOnlyBundleFromConsolidated: boolean
  ) => void;
};

type HeaderParams = {
  patient: PatientDTO | undefined;
  isFetchingPatient: boolean;
  isQueryingDocuments: boolean;
  isFetchingDiff: boolean;
  isAtLeastOneMrDownloading: boolean;
  isHieOptedOut: boolean;
  usingSectionSearch: boolean;
  usingMetriportOnlyBundleFromConsolidated: boolean;
  selectedTab: PatientTab;
  lastDocQuery: string | undefined;
  isLimited?: boolean;
  actions: HeaderActions;
  setHeaderHeight: (height: number) => void;
};

export function PatientHeader({
  patient,
  isFetchingPatient,
  isQueryingDocuments,
  isFetchingDiff,
  isAtLeastOneMrDownloading,
  isHieOptedOut,
  usingSectionSearch,
  usingMetriportOnlyBundleFromConsolidated,
  lastDocQuery,
  isLimited,
  actions,
  setHeaderHeight,
}: HeaderParams) {
  const { elementRef, updateHeight } = useRefAndUpdateHeight(setHeaderHeight);
  useEffect(() => {
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  return (
    <Box ref={elementRef} bg={useColorModeValue("gray.100", "gray.900")}>
      <Box
        bg={useColorModeValue("white", "gray.800")}
        border={"solid"}
        borderRadius={10}
        borderColor={useColorModeValue("gray.200", "gray.700")}
        px={3}
        py={2}
      >
        <Flex justifyContent="space-between" alignItems="baseline" gap={2}>
          <Flex alignItems="center" flexDirection="column" textAlign="left">
            <PatientInfo
              patient={patient}
              isLoading={isFetchingPatient}
              actions={isLimited ? undefined : actions}
            />
            <PatientDemo
              patient={patient}
              isLoading={isFetchingPatient}
              actions={actions}
              lastDocQuery={lastDocQuery}
            />
          </Flex>
          {isLimited ? (
            <Flex>
              {actions.setUsingMetriportOnlyBundleFromConsolidated && (
                <EhrDiffButton
                  isFetchingDiff={isFetchingDiff}
                  isQueryingDocuments={isQueryingDocuments}
                  usingMetriportOnlyBundleFromConsolidated={
                    usingMetriportOnlyBundleFromConsolidated
                  }
                  actions={actions}
                />
              )}
              <PatientMenuActionsLimited
                usingSectionSearch={usingSectionSearch}
                actions={actions}
                isQueryingDocuments={isQueryingDocuments}
              />
            </Flex>
          ) : (
            <Flex>
              <PatientButtonActions
                isAtLeastOneMrDownloading={isAtLeastOneMrDownloading}
                isQueryingDocuments={isQueryingDocuments}
                actions={actions}
              />
              <PatientMenuActionsFull
                usingSectionSearch={usingSectionSearch}
                isHieOptedOut={isHieOptedOut}
                actions={actions}
              />
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}

function EhrDiffButton({
  isFetchingDiff,
  isQueryingDocuments,
  usingMetriportOnlyBundleFromConsolidated,
  actions,
}: {
  isFetchingDiff: boolean;
  isQueryingDocuments: boolean;
  usingMetriportOnlyBundleFromConsolidated: boolean;
  actions: Pick<HeaderActions, "setUsingMetriportOnlyBundleFromConsolidated">;
}) {
  const switchSize = useBreakpointValue({ base: "sm", md: "md" });
  return (
    <Flex alignItems={"center"} mr={{ base: 2, md: 0 }}>
      <Tooltip label="Show data that is only present in Metriport and not in your EHR">
        <FormControl display="flex" alignItems="center">
          <FormLabel
            htmlFor="delta"
            fontSize={{ base: "xs", md: "sm" }}
            mb={0}
            mr={1}
            fontWeight="semibold"
          >
            Delta
          </FormLabel>
          <Switch
            id="delta"
            size={switchSize}
            isChecked={usingMetriportOnlyBundleFromConsolidated}
            onChange={() => {
              actions.setUsingMetriportOnlyBundleFromConsolidated?.(
                !usingMetriportOnlyBundleFromConsolidated
              );
            }}
            colorScheme={"blue"}
            isDisabled={isFetchingDiff || isQueryingDocuments}
          />
        </FormControl>
      </Tooltip>
    </Flex>
  );
}

function HighRiskRenderer(isHighRisk: boolean | undefined) {
  return isHighRisk ? <HighRiskBadge /> : null;
}

function PatientInfo({
  patient,
  isLoading,
  actions,
}: {
  patient: PatientDTO | undefined;
  isLoading: boolean;
  actions?: Pick<HeaderActions, "onEditPatient">;
}) {
  const { sharedState } = useSharedContext();
  const patientName = `${patient?.firstName} ${patient?.lastName}`;
  return (
    <Skeleton display={"flex"} w="100%" isLoaded={!isLoading}>
      <Flex ml={2}>
        <Flex alignItems={"center"} gap={2}>
          <Text fontSize={{ base: "md", md: "lg" }} fontWeight={"bold"}>
            {patientName}
          </Text>
          {HighRiskRenderer(sharedState.isMetriportUser)}
        </Flex>
        {actions && (
          <IconButton
            onClick={actions.onEditPatient}
            bg={"transparent"}
            _hover={{ color: "#879ced" }}
            aria-label="Edit Patient"
            icon={<EditIcon />}
          />
        )}
      </Flex>
    </Skeleton>
  );
}

function PatientDemo({
  patient,
  isLoading,
  actions,
  lastDocQuery,
}: {
  patient: PatientDTO | undefined;
  isLoading: boolean;
  actions: Pick<HeaderActions, "onDisplayPatientDemos">;
  lastDocQuery: string | undefined;
}) {
  const demoFields: Array<{ label: string; value: string | undefined }> = [
    { label: "id", value: patient?.id },
    { label: "dob", value: patient?.dob },
    { label: "gender", value: patient?.genderAtBirth },
  ];
  const borderColor = useColorModeValue("gray.300", "gray.600");

  return (
    <Skeleton display={"flex"} isLoaded={!isLoading} w="100%">
      <Flex whiteSpace={"nowrap"} py={2} ml={2} flexDirection="column">
        <Flex alignItems={"center"}>
          {demoFields.map(({ label, value }, i) => (
            <Flex key={i}>
              {label === "id" ? (
                <IdContainer fontSize={{ base: "xs", md: "sm" }} value={value} entity={"Patient"} />
              ) : (
                <Text fontSize={{ base: "xs", md: "sm" }}>{value}</Text>
              )}
              <Divider
                orientation="vertical"
                color={borderColor}
                height="1rem"
                borderWidth="2px"
                mx={2}
              />
            </Flex>
          ))}
          <Flex>
            <Icon
              onClick={actions.onDisplayPatientDemos}
              _hover={{ color: "#879ced" }}
              cursor={"pointer"}
              aria-label="Patient Demographics"
              as={FiInfo}
              fontSize="lg"
            />
          </Flex>
        </Flex>
        {lastDocQuery && (
          <Flex>
            <Text fontSize={{ base: "xs", md: "sm" }} color="fg.muted">
              Last Refreshed: {lastDocQuery}
            </Text>
          </Flex>
        )}
      </Flex>
    </Skeleton>
  );
}

function PatientButtonActions({
  isAtLeastOneMrDownloading,
  isQueryingDocuments,
  actions,
}: {
  isAtLeastOneMrDownloading: boolean;
  isQueryingDocuments: boolean;
  actions: Pick<HeaderActions, "onGenerateMedicalRecord" | "onQueryPatientDocuments">;
}) {
  const buttonSize = useBreakpointValue({ base: "xs", lg: "sm" });
  return (
    <Flex flexDirection={{ base: "column", xl: "row" }} gap={2}>
      <MetriportButton
        size={buttonSize}
        isLoading={isAtLeastOneMrDownloading}
        onClick={actions.onGenerateMedicalRecord}
        whiteSpace={"wrap"}
        height="auto"
        py={1}
      >
        Generate Medical Record
      </MetriportButton>
      <MetriportButton
        size={buttonSize}
        isLoading={isQueryingDocuments}
        onClick={actions.onQueryPatientDocuments}
        variant={"outline"}
        whiteSpace={"wrap"}
        height="auto"
        py={1}
      >
        Refresh Patient Data
      </MetriportButton>
    </Flex>
  );
}

function PatientMenuActionsFull({
  usingSectionSearch,
  isHieOptedOut,
  actions,
}: {
  isHieOptedOut?: boolean;
  usingSectionSearch: boolean;
  actions: Pick<
    HeaderActions,
    | "onViewPatientMatches"
    | "onSetPatientTab"
    | "toggleHieOptOut"
    | "toggleUsingSectionSearch"
    | "onEditCustomKeywords"
  >;
}) {
  const bg = useColorModeValue("gray.50", "gray.800");
  const menuButtonSize = useBreakpointValue({ base: "xs", lg: "sm" });
  return (
    <Box ml={2}>
      <Menu placement="bottom-end">
        <MenuButton
          size={menuButtonSize}
          as={IconButton}
          aria-label="Options"
          icon={<BsThreeDotsVertical />}
          variant="outline"
        />
        <MenuList bg={bg}>
          {actions.toggleUsingSectionSearch && (
            <MenuItem bg={bg} onClick={() => actions.toggleUsingSectionSearch?.()}>
              {usingSectionSearch ? "Disable " : "Enable "} Search Per Section
            </MenuItem>
          )}
          {actions.onEditCustomKeywords && (
            <MenuItem bg={bg} onClick={actions.onEditCustomKeywords}>
              Edit Custom Keywords
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              actions.onSetPatientTab("matches");
              actions.onViewPatientMatches();
            }}
            bg={bg}
          >
            View Patient Matches
          </MenuItem>
          <MenuItem onClick={() => actions.toggleHieOptOut(!isHieOptedOut)} bg={bg}>
            {isHieOptedOut ? "Opt In to" : "Opt Out of"} Networks
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
}

function PatientMenuActionsLimited({
  usingSectionSearch,
  actions,
  isQueryingDocuments,
}: {
  usingSectionSearch: boolean;
  actions: Pick<
    HeaderActions,
    | "onGenerateMedicalRecord"
    | "onQueryPatientDocuments"
    | "toggleUsingSectionSearch"
    | "onSetPatientTab"
    | "onViewPatientMatches"
    | "onEditCustomKeywords"
  >;
  isQueryingDocuments: boolean;
}) {
  const bg = useColorModeValue("gray.50", "gray.800");
  const menuButtonSize = useBreakpointValue({ base: "xs", lg: "sm" });
  return (
    <Box ml={2}>
      <Menu placement="bottom-end">
        <MenuButton
          size={menuButtonSize}
          as={IconButton}
          aria-label="Options"
          icon={<BsThreeDotsVertical />}
          variant="outline"
        />
        <MenuList bg={bg}>
          {actions.toggleUsingSectionSearch && (
            <MenuItem bg={bg} onClick={() => actions.toggleUsingSectionSearch?.()}>
              {usingSectionSearch ? "Disable " : "Enable "} Search Per Section
            </MenuItem>
          )}
          {actions.onEditCustomKeywords && (
            <MenuItem bg={bg} onClick={actions.onEditCustomKeywords}>
              Edit Custom Keywords
            </MenuItem>
          )}
          <MenuItem
            isDisabled={isQueryingDocuments}
            onClick={actions.onQueryPatientDocuments}
            bg={bg}
          >
            Refresh Patient Data
          </MenuItem>
          <MenuItem
            onClick={() => {
              actions.onSetPatientTab("matches");
              actions.onViewPatientMatches();
            }}
            bg={bg}
          >
            View Patient Matches
          </MenuItem>
          <MenuItem onClick={actions.onGenerateMedicalRecord} bg={bg}>
            Generate Medical Record
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
}

export function HighRiskBadge() {
  return (
    <Box
      as="span"
      px="2"
      py="0.5"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="full"
      fontSize="2xs"
      fontWeight="900"
      textTransform="uppercase"
      letterSpacing="0.05em"
      color="white"
      bg={"red"}
      m="2"
      transition="all 0.2s ease"
    >
      High Risk
    </Box>
  );
}
